import { ref, computed } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { useSdk } from './useSdk';
import { queryKeys } from '@/queries/queryKeys';
import type { Playlist } from '@cc-consulting-nv/ccsdk';

/**
 * Playlist edit mode context - stored in sessionStorage for persistence
 */
interface PlaylistEditContext {
  playlistId: string;
  playlistName: string;
  playlistCover?: string;
  songsAdded: number;
  /** Track which songs have been added in this session to prevent duplicates */
  addedSongUlids: string[];
}

const STORAGE_KEY = 'playlist-edit-mode';

// Module-level reactive state (singleton pattern like useActingContext)
const editContext = ref<PlaylistEditContext | null>(null);

/**
 * Composable for managing playlist edit mode.
 * Allows administrators to browse songs and quickly add them to a selected playlist.
 * Similar pattern to useActingContext but without time limits.
 */
export function usePlaylistEditMode() {
  const { sdk } = useSdk();
  const queryClient = useQueryClient();

  const isEditingPlaylist = computed(() => editContext.value !== null);

  const playlistId = computed(() => editContext.value?.playlistId ?? null);
  const playlistName = computed(() => editContext.value?.playlistName ?? '');
  const playlistCover = computed(() => editContext.value?.playlistCover ?? null);
  const songsAdded = computed(() => editContext.value?.songsAdded ?? 0);

  /**
   * Check if a song has already been added in this session
   */
  function isSongAdded(songUlid: string): boolean {
    return editContext.value?.addedSongUlids.includes(songUlid) ?? false;
  }

  /**
   * Load edit context from sessionStorage on init
   */
  function loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as PlaylistEditContext;
        editContext.value = parsed;
      }
    } catch (err) {
      console.error('[usePlaylistEditMode] Failed to load from storage:', err);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Save current context to sessionStorage
   */
  function saveToStorage() {
    if (editContext.value) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(editContext.value));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Enter playlist edit mode for a specific playlist
   */
  function enterEditMode(playlist: Playlist) {
    // Extract cover image (API may return snake_case or camelCase)
    const playlistAny = playlist as Record<string, unknown>;
    const cover = (playlistAny.cover_image || playlistAny.coverImage || null) as string | null;

    // API expects ulid for endpoints - try ulid first, then id, then playlist_id
    const playlistId = playlist.ulid || playlist.id || (playlistAny.playlist_id as string);

    if (!playlistId) {
      console.error('[usePlaylistEditMode] No playlist ID found:', playlist);
      return;
    }

    editContext.value = {
      playlistId,
      playlistName: playlist.name,
      playlistCover: cover ?? undefined,
      songsAdded: 0,
      addedSongUlids: [],
    };

    saveToStorage();
    console.log('[usePlaylistEditMode] Entered edit mode for playlist:', playlist.name, 'ID:', playlistId);
  }

  /**
   * Exit playlist edit mode
   */
  function exitEditMode() {
    const wasEditing = editContext.value?.playlistName;
    editContext.value = null;
    saveToStorage();
    console.log('[usePlaylistEditMode] Exited edit mode for playlist:', wasEditing);
  }

  /**
   * Add a song to the currently editing playlist
   * Returns true on success, false on failure
   */
  async function addSongToPlaylist(songUlid: string): Promise<boolean> {
    if (!editContext.value || !sdk) {
      console.warn('[usePlaylistEditMode] Cannot add song - not in edit mode or SDK not available');
      return false;
    }

    // Check if already added in this session
    if (editContext.value.addedSongUlids.includes(songUlid)) {
      console.log('[usePlaylistEditMode] Song already added in this session:', songUlid);
      return true; // Already added, consider it success
    }

    try {
      await sdk.addPlaylistSongs(editContext.value.playlistId, [{ id: songUlid }]);

      // Update context
      editContext.value.songsAdded += 1;
      editContext.value.addedSongUlids.push(songUlid);
      saveToStorage();

      // Invalidate playlist queries so UI updates with new song count
      queryClient.invalidateQueries({
        queryKey: queryKeys.playlists.detail(editContext.value.playlistId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.playlists.all });

      console.log('[usePlaylistEditMode] Added song to playlist:', songUlid);
      return true;
    } catch (err) {
      console.error('[usePlaylistEditMode] Failed to add song:', err);
      return false;
    }
  }

  // Initialize from storage on first use
  if (editContext.value === null) {
    loadFromStorage();
  }

  return {
    // State
    editContext: computed(() => editContext.value),
    isEditingPlaylist,
    playlistId,
    playlistName,
    playlistCover,
    songsAdded,

    // Methods
    isSongAdded,
    enterEditMode,
    exitEditMode,
    addSongToPlaylist,
  };
}
